import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import { getTerms } from '../../lib/api/user/userApi';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';

const PrivacyContainer = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const SectionContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const WebSectionTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 24px;
  text-align: center;
`;

const ContentContainer = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  white-space: pre-wrap;
  word-break: break-word;
  
  h1, h2, h3, h4, h5, h6 {
    color: #333;
    margin: 20px 0 10px 0;
    font-weight: 600;
  }
  
  h1 { font-size: 20px; }
  h2 { font-size: 18px; }
  h3 { font-size: 16px; }
  
  p {
    margin: 10px 0;
  }
  
  ul, ol {
    margin: 10px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 5px 0;
  }
  
  strong, b {
    font-weight: 600;
    color: #333;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 15px 0;
  }
  
  th, td {
    border: 1px solid #ddd;
    padding: 8px 12px;
    text-align: left;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #333;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #666;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #999;
  font-size: 16px;
`;

// 다국어 텍스트 객체
const texts = {
  ko: {
    privacyPolicy: '개인정보처리방침',
    loading: '개인정보처리방침을 불러오는 중...',
    loadError: '개인정보처리방침을 불러올 수 없습니다.',
    loadFailed: '개인정보처리방침을 불러오는데 실패했습니다.'
  },
  en: {
    privacyPolicy: 'Privacy Policy',
    loading: 'Loading privacy policy...',
    loadError: 'Unable to load privacy policy.',
    loadFailed: 'Failed to load privacy policy.'
  },
};

function PrivacyPolicy() {
  const navigate = useNavigate();
  const { lang } = useLang();

  const t = texts[lang];
  
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // 개인정보처리방침 데이터 불러오기
  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setIsLoading(true);
        setError(false);
        
        const termLang = lang === 'ko' ? 'KOR' : 'ENG';
        const response = await getTerms({ termLang });
        
        if (response.data.statusCode === 200 && response.data.data) {
          const termsArray = response.data.data as any[];
          
          // 언어에 따라 개인정보처리방침 인덱스 매핑
          let privacyIndex;
          if (lang === 'ko') {
            // KOR: 1(privacy)
            privacyIndex = termsArray.find((term: any) => term.index === 1);
          } else {
            // ENG: 2(privacy)
            privacyIndex = termsArray.find((term: any) => term.index === 2);
          }

          if (privacyIndex && privacyIndex.content) {
            setPrivacyContent(privacyIndex.content);
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      } catch (error) {
        devError('개인정보처리방침 조회 실패:', error);
        toast.error(t.loadFailed);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, [lang, t.loadFailed]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          {t.loading}
        </LoadingContainer>
      );
    }

    if (error || !privacyContent) {
      return (
        <ErrorContainer>
          {t.loadError}
        </ErrorContainer>
      );
    }

    return (
      <ContentContainer 
        dangerouslySetInnerHTML={{ __html: privacyContent }}
      />
    );
  };

  return (
    <MainLayout>
      <CenteredLayout>
        <ProjectAppbar 
          title={t.privacyPolicy}
          showBackButton={true}
          onBack={() => navigate('/')}
        />
        {/* <PrivacyContainer> */}
          <SectionContainer>
            <WebSectionTitle>{t.privacyPolicy}</WebSectionTitle>
            {renderContent()}
          </SectionContainer>
        {/* </PrivacyContainer> */}
      </CenteredLayout>
    </MainLayout>
  );
}

export default PrivacyPolicy;
